import React, { useState, useMemo, useCallback } from 'react'
import { Search, Filter, X, Star, TrendingUp } from 'lucide-react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Card, CardContent } from '../ui/card'
import { CleanTeamCard } from './CleanTeamCard'
import { CompactTeamCard } from './CompactTeamCard'
import { TeamDetailModal } from './team-detail-modal'
import { FilterDrawer } from '../ui/filter-drawer'
import { SearchSuggestions } from '../ui/search-suggestions'
import { PullToRefreshIndicator } from '../ui/pull-to-refresh-indicator'
import { useTeams } from '../../hooks/useTeams'
import { useUserSpots } from '../../hooks/useUserSpots'
import { useToast } from '../../hooks/use-toast'
import { usePullToRefresh } from '../../hooks/usePullToRefresh'
import { useRecentSearches } from '../../hooks/useRecentSearches'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import {
  useDebounce,
  useOptimizedSearch,
  useOptimizedFilters,
  useTouchOptimization,
  useMemoryCleanup,
  usePerformanceMonitoring
} from '../../utils/mobileOptimizations'
import type { Team } from '../../types'

interface QuickFilter {
  id: string
  label: string
  count: number
  active: boolean
}

export function SimplifiedTeamBrowser() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [showAllFilters, setShowAllFilters] = useState(false)
  const [teamsSortBy, setTeamsSortBy] = useState<'alphabetical' | 'league' | 'founded'>('alphabetical')
  const [isMobile, setIsMobile] = useState(false)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Performance optimizations
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const optimizedFilters = useOptimizedFilters()
  useTouchOptimization()
  useMemoryCleanup()
  usePerformanceMonitoring()

  // Hooks
  const { teams, loading, searchTeams } = useTeams()
  const { spotTeam, isTeamSpotted, isTeamSpotting } = useUserSpots()
  const { toast } = useToast()
  const { recentSearches, addRecentSearch, removeRecentSearch, clearRecentSearches } = useRecentSearches()

  // Pull to refresh
  const { pullDistance, isRefreshing } = usePullToRefresh({
    onRefresh: async () => {
      await searchTeams()
      toast({
        title: "Refreshed",
        description: "Teams list has been updated.",
        duration: 2000,
      })
    },
    disabled: !isMobile
  })
  
  // Local spotting state for immediate feedback
  const [spottingTeams, setSpottingTeams] = useState<Set<string>>(new Set())
  
  // Optimized search with debouncing
  const searchFilteredTeams = useOptimizedSearch(
    teams, 
    debouncedSearchTerm, 
    ['name', 'city', 'league', 'sport']
  )

  // Generate quick filters from team data (memoized for performance)
  const quickFilters = useMemo(() => {
    const sportCounts = new Map<string, number>()
    const popularLeagues = new Map<string, number>()
    
    // Sport emoji mapping
    const sportEmojis: Record<string, string> = {
      'Soccer': '⚽',
      'Basketball': '🏀',
      'Football': '🏈',
      'Baseball': '⚾',
      'Hockey': '🏒',
      'Racing': '🏎️',
      'College': '🎓',
      'NHL': '🏒',
      'MLB': '⚾'
    }
    
    teams.forEach(team => {
      // Count sports
      sportCounts.set(team.sport, (sportCounts.get(team.sport) || 0) + 1)
      
      // Count popular leagues (those with many teams)
      popularLeagues.set(team.league, (popularLeagues.get(team.league) || 0) + 1)
    })
    
    // Debug sport counts to see if Racing is being detected
    if (teams.length > 600) { // Only log when we have most teams loaded
      console.log('All sport counts:', Object.fromEntries(sportCounts.entries()))
      console.log('Teams with Racing sport:', teams.filter(team => team.sport === 'Racing').map(t => ({ name: t.name, sport: t.sport, league: t.league })))
    }
    

    const filters: QuickFilter[] = []

    // Add top sports - include Racing specifically
    const sortedSports = Array.from(sportCounts.entries())
      .sort((a, b) => b[1] - a[1])
    
    // Always include Racing if it exists, even if not in top 5
    const racingSport = sortedSports.find(([sport]) => sport === 'Racing')
    const topSports = sortedSports.slice(0, 5)
    
    // Add Racing to top sports if not already included
    if (racingSport && !topSports.some(([sport]) => sport === 'Racing')) {
      topSports.push(racingSport)
    }
    
    topSports.forEach(([sport, count]) => {
      const filterId = `sport-${sport.toLowerCase()}`
      const emoji = sportEmojis[sport] || '🏆'
      filters.push({
        id: filterId,
        label: `${emoji} ${sport}`,
        count,
        active: optimizedFilters.filters.has(filterId)
      })
    })

    // Only show sport filters to keep it simple
    
    // Debug: Check if Racing is in the final filters
    if (teams.length > 600) {
      console.log('Generated filters:', filters.map(f => ({ label: f.label, count: f.count })))
    }

    return filters
  }, [teams, optimizedFilters.filters])

  // Generate search suggestions based on team data
  const searchSuggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return []

    const suggestions = new Set<string>()
    const lowerSearch = searchTerm.toLowerCase()

    teams.forEach(team => {
      // Add team name if it matches
      if (team.name.toLowerCase().includes(lowerSearch) && team.name.toLowerCase() !== lowerSearch) {
        suggestions.add(team.name)
      }
      // Add city if it matches
      if (team.city?.toLowerCase().includes(lowerSearch) && team.city.toLowerCase() !== lowerSearch) {
        suggestions.add(team.city)
      }
      // Add league if it matches
      if (team.league?.toLowerCase().includes(lowerSearch) && team.league.toLowerCase() !== lowerSearch) {
        suggestions.add(team.league)
      }
    })

    return Array.from(suggestions).slice(0, 5)
  }, [searchTerm, teams])

  // Filter and sort teams based on search, quick filters, and sorting option
  const filteredTeams = useMemo(() => {
    let filtered = searchFilteredTeams

    // Apply quick filters
    if (optimizedFilters.hasFilters) {
      filtered = filtered.filter(team => {
        return optimizedFilters.filterArray.some(([filterId]) => {
          if (filterId.startsWith('sport-')) {
            const sport = filterId.replace('sport-', '')
            return team.sport.toLowerCase() === sport
          }
          if (filterId.startsWith('league-')) {
            const league = filterId.replace('league-', '').replace(/-/g, ' ')
            return team.league.toLowerCase() === league
          }
          return false
        })
      })
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (teamsSortBy) {
        case 'alphabetical':
          return (a.name || '').localeCompare(b.name || '')
        case 'league':
          // Sort by league first, then by name within league
          const leagueComparison = (a.league || '').localeCompare(b.league || '')
          return leagueComparison !== 0 ? leagueComparison : (a.name || '').localeCompare(b.name || '')
        case 'founded':
          // Sort by founded year (newest first), then by name
          const foundedComparison = (b.founded || 0) - (a.founded || 0)
          return foundedComparison !== 0 ? foundedComparison : (a.name || '').localeCompare(b.name || '')
        default:
          return (a.name || '').localeCompare(b.name || '')
      }
    })

    return sorted
  }, [searchFilteredTeams, optimizedFilters.filterArray, optimizedFilters.hasFilters, teamsSortBy])

  // Optimized handlers with useCallback
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
    setShowSearchSuggestions(value.length > 0)
    // Debounced search will handle the actual search
  }, [])

  const handleSelectSearch = useCallback((search: string) => {
    setSearchTerm(search)
    setShowSearchSuggestions(false)
    addRecentSearch(search)
  }, [addRecentSearch])

  const handleQuickFilterToggle = useCallback((filterId: string) => {
    if (optimizedFilters.filters.has(filterId)) {
      optimizedFilters.removeFilter(filterId)
    } else {
      optimizedFilters.addFilter(filterId, true)
    }
  }, [optimizedFilters])

  const handleClearFilters = useCallback(() => {
    setSearchTerm('')
    optimizedFilters.clearFilters()
    searchTeams()
  }, [optimizedFilters, searchTeams])

  const handleSpotTeam = useCallback(async (teamId: string) => {
    // Find the full team object
    const team = teams.find(t => t.id === teamId)
    if (!team) {
      toast({
        title: "Error",
        description: "Team not found. Please try again.",
        variant: "destructive",
        duration: 3000,
      })
      return
    }

    // Add to local spotting state for immediate feedback
    setSpottingTeams(prev => new Set(prev).add(teamId))
    
    try {
      // Pass full team data for immediate UI updates
      await spotTeam(team.id, team)
      
      toast({
        title: "Team Spotted!",
        description: `${team.name} has been added to your spotted teams.`,
        duration: 3000,
      })
    } catch (error) {
      console.error('Error spotting team:', error)
      toast({
        title: "Error",
        description: `Failed to spot ${team.name}. Please try again.`,
        variant: "destructive",
        duration: 3000,
      })
    } finally {
      // Remove from local spotting state
      setSpottingTeams(prev => {
        const newSet = new Set(prev)
        newSet.delete(teamId)
        return newSet
      })
    }
  }, [spotTeam, teams, toast])

  const handleViewTeamDetails = useCallback((team: Team) => {
    setSelectedTeam(team)
  }, [])

  const hasActiveFilters = searchTerm.trim() || optimizedFilters.hasFilters

  // Keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: '/',
      description: 'Focus search',
      action: () => {
        searchInputRef.current?.focus()
      }
    },
    {
      key: 'Escape',
      description: 'Clear search or close modals',
      action: () => {
        if (selectedTeam) {
          setSelectedTeam(null)
        } else if (isFilterDrawerOpen) {
          setIsFilterDrawerOpen(false)
        } else if (searchTerm) {
          handleSearch('')
          searchInputRef.current?.blur()
        }
      }
    },
    {
      key: 'f',
      ctrl: true,
      description: 'Open filters',
      action: () => {
        if (isMobile) {
          setIsFilterDrawerOpen(true)
        }
      }
    },
    {
      key: 'r',
      ctrl: true,
      description: 'Refresh teams',
      action: async () => {
        await searchTeams()
        toast({
          title: "Refreshed",
          description: "Teams list has been updated.",
          duration: 2000,
        })
      }
    }
  ], !selectedTeam && !isFilterDrawerOpen)

  // Effect for debounced search
  React.useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      searchTeams(debouncedSearchTerm)
    } else {
      searchTeams()
    }
  }, [debouncedSearchTerm, searchTeams])

  // Mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize() // Initial check
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Pull to Refresh Indicator */}
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
      />

      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">Find Your Team</h1>
        <p className="text-gray-600">Search through hundreds of teams or browse by sport and league</p>
      </div>

      {/* Search Bar - Sticky Static */}
      <div className="sticky top-0 z-30 bg-gray-50 pb-4 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="max-w-2xl mx-auto pt-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search teams by name, city, league, or sport..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => setShowSearchSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
              className="pl-12 pr-12 text-base h-14 md:h-14 min-h-[48px] md:text-lg border-2 border-gray-200 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 shadow-md rounded-xl bg-white"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  handleSearch('')
                  setShowSearchSuggestions(false)
                }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 min-h-[40px] min-w-[40px] h-10 w-10 p-0 rounded-full hover:bg-gray-100"
              >
                <X className="h-4 w-4 text-gray-400" />
              </Button>
            )}

            {/* Search Suggestions */}
            <SearchSuggestions
              isOpen={showSearchSuggestions}
              searchTerm={searchTerm}
              recentSearches={recentSearches}
              suggestions={searchSuggestions}
              onSelectSearch={handleSelectSearch}
              onRemoveRecent={removeRecentSearch}
              onClearRecent={clearRecentSearches}
            />
          </div>
        </div>
      </div>

      {/* Quick Filters - Desktop Only */}
      <div className="hidden md:flex flex-wrap items-center justify-center gap-3">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Filter className="h-4 w-4" />
          <span>Quick filters:</span>
        </div>

        {quickFilters.slice(0, showAllFilters ? quickFilters.length : 8).map((filter) => (
          <Button
            key={filter.id}
            variant={filter.active ? "default" : "outline"}
            size="sm"
            onClick={() => handleQuickFilterToggle(filter.id)}
            className="flex items-center space-x-2"
          >
            <span>{filter.label}</span>
            <Badge variant="secondary" className="text-xs ml-1">
              {filter.count}
            </Badge>
          </Button>
        ))}

        {quickFilters.length > 8 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllFilters(!showAllFilters)}
            className="text-blue-600 hover:text-blue-800"
          >
            {showAllFilters ? 'Show Less' : `+${quickFilters.length - 8} More`}
          </Button>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-red-600 hover:text-red-800 flex items-center space-x-1"
          >
            <X className="h-3 w-3" />
            <span>Clear All</span>
          </Button>
        )}
      </div>

      {/* Mobile Filter Button */}
      <div className="md:hidden flex justify-center">
        <Button
          onClick={() => setIsFilterDrawerOpen(true)}
          variant="outline"
          className="min-h-[48px] px-6 flex items-center space-x-2"
        >
          <Filter className="h-5 w-5" />
          <span>Filters</span>
          {optimizedFilters.filterArray.length > 0 && (
            <Badge className="ml-2 bg-[#42a5f5]">
              {optimizedFilters.filterArray.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Mobile Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={quickFilters}
        onToggleFilter={handleQuickFilterToggle}
        onClearAll={handleClearFilters}
        activeFiltersCount={optimizedFilters.filterArray.length}
      />

      {/* Sort Options */}
      {filteredTeams.length > 0 && (
        <div className="flex items-center justify-center space-x-3">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <TrendingUp className="h-4 w-4" />
            <span>Sort:</span>
          </div>
          <select 
            value={teamsSortBy} 
            onChange={(e) => setTeamsSortBy(e.target.value as 'alphabetical' | 'league' | 'founded')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="alphabetical">Alphabetical (A-Z)</option>
            <option value="league">By League</option>
            <option value="founded">By Year Founded</option>
          </select>
        </div>
      )}

      {/* Results Summary */}
      <div className="text-center">
        {loading ? (
          <div className="flex items-center justify-center space-x-2 text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
            <span>Searching teams...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <span>
              Showing <strong>{filteredTeams.length}</strong> of <strong>{teams.length}</strong> teams
            </span>
            {hasActiveFilters && (
              <div className="flex items-center space-x-1">
                <Filter className="h-3 w-3" />
                <span>Filtered results</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Teams Grid - Dense grid for tablet/desktop, list for mobile */}
      {loading ? (
        <div className={isMobile
          ? "space-y-3"
          : "grid grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 desktop:grid-cols-5 gap-3"
        }>
          {[...Array(isMobile ? 4 : 10)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className={isMobile ? "p-6 space-y-4" : "p-3 space-y-3"}>
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className={`bg-gray-300 rounded ${isMobile ? 'h-5 w-3/4' : 'h-4 w-full'}`}></div>
                    <div className={`bg-gray-300 rounded ${isMobile ? 'h-4 w-1/2' : 'h-3 w-2/3'}`}></div>
                  </div>
                  <div className={`bg-gray-300 rounded ${isMobile ? 'h-12 w-12' : 'h-10 w-10'}`}></div>
                </div>
                {!isMobile && (
                  <>
                    <div className="flex gap-1">
                      <div className="h-5 bg-gray-300 rounded w-16"></div>
                      <div className="h-5 bg-gray-300 rounded w-12"></div>
                    </div>
                    <div className="flex gap-1">
                      <div className="h-8 bg-gray-300 rounded flex-1"></div>
                      <div className="h-8 bg-gray-300 rounded flex-1"></div>
                    </div>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : filteredTeams.length > 0 ? (
        <div className={isMobile
          ? "space-y-3"
          : "grid grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 desktop:grid-cols-5 gap-3"
        }>
          {filteredTeams.map((team) => {
            const TeamCardComponent = isMobile ? CleanTeamCard : CompactTeamCard
            return (
              <TeamCardComponent
                key={team.id}
                team={team}
                isSpotted={isTeamSpotted(team.id)}
                onSpot={handleSpotTeam}
                onViewDetails={handleViewTeamDetails}
                spotButtonState={{
                  text: spottingTeams.has(team.id) ? "Spotting..." : isTeamSpotted(team.id) ? "Spotted" : "Spot",
                  variant: isTeamSpotted(team.id) ? "secondary" : "default",
                  disabled: isTeamSpotted(team.id) || spottingTeams.has(team.id),
                  loading: spottingTeams.has(team.id) || (isTeamSpotting && isTeamSpotting(team.id)),
                  isSpotted: isTeamSpotted(team.id)
                }}
                showSpottedIndicator={true}
              />
            )
          })}
        </div>
      ) : (
        <div className="text-center py-16 space-y-4">
          <div className="text-6xl">🔍</div>
          <h3 className="text-xl font-semibold text-gray-900">No teams found</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            {hasActiveFilters 
              ? "Try adjusting your search or removing some filters to see more results."
              : "No teams are currently available."
            }
          </p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={handleClearFilters}>
              Clear All Filters
            </Button>
          )}
        </div>
      )}

      {/* Popular Teams Section - Only show when no filters */}
      {!hasActiveFilters && !loading && filteredTeams.length > 0 && (
        <div className="border-t pt-8">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Popular Teams</h2>
          </div>
          <div className={isMobile
            ? "space-y-3"
            : "grid grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 desktop:grid-cols-5 gap-3"
          }>
            {filteredTeams
              .filter(team => ['Lakers', 'Warriors', 'Cowboys', 'Patriots', 'Yankees', 'Dodgers'].some(popular =>
                team.name.includes(popular)
              ))
              .slice(0, isMobile ? 4 : 10)
              .map((team) => {
                const TeamCardComponent = isMobile ? CleanTeamCard : CompactTeamCard
                return (
                  <TeamCardComponent
                    key={team.id}
                    team={team}
                    isSpotted={isTeamSpotted(team.id)}
                    onSpot={handleSpotTeam}
                    onViewDetails={handleViewTeamDetails}
                    spotButtonState={{
                      text: spottingTeams.has(team.id) ? "Spotting..." : isTeamSpotted(team.id) ? "Spotted" : "Spot",
                      variant: isTeamSpotted(team.id) ? "secondary" : "default",
                      disabled: isTeamSpotted(team.id) || spottingTeams.has(team.id),
                      loading: spottingTeams.has(team.id) || (isTeamSpotting && isTeamSpotting(team.id)),
                      isSpotted: isTeamSpotted(team.id)
                    }}
                    showSpottedIndicator={true}
                  />
                )
              })}
          </div>
        </div>
      )}

      {/* Team Detail Modal */}
      <TeamDetailModal
        team={selectedTeam}
        isOpen={selectedTeam !== null}
        onClose={() => setSelectedTeam(null)}
      />
    </div>
  )
}